import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { adminAPI, speakersAPI } from "../../services/api";
import { Plus, Users, Clock, Edit2, Trash2, X } from "lucide-react";
import Modal from "../../components/Modal";
import "./Admin.css";

const MasterclassManager = () => {
  const { eventId } = useParams();
  const [masterclasses, setMasterclasses] = useState([]);
  const [speakersList, setSpeakersList] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Form
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    instructor_ids: [],
    day: 1,
    start_time: "", // ISO string or datetime-local value
    end_time: "",
    location: "",
    capacity: 30,
    prerequisites: "None",
    materials: "None",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mcRes, spRes, eventRes] = await Promise.all([
        adminAPI.getMasterclasses(eventId),
        adminAPI.getSpeakers(),
        eventId ? adminAPI.getEvent(eventId) : Promise.resolve({ data: null }),
      ]);
      setMasterclasses(mcRes.data);
      setSpeakersList(spRes.data);
      setCurrentEvent(eventRes.data);
    } catch (err) {
      console.error("Failed to load data", err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddNew = () => {
    setFormData({
      title: "",
      description: "",
      instructor_ids: [],
      day: 1,
      start_time: "",
      end_time: "",
      location: "",
      capacity: 30,
      prerequisites: "None",
      materials: "None",
    });
    setIsEditing(false);
    setCurrentId(null);
    setShowModal(true);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Format to YYYY-MM-DDTHH:mm using local time components to preserve wall-clock time
    const pad = (n) => n.toString().padStart(2, "0");
    const YYYY = date.getFullYear();
    const MM = pad(date.getMonth() + 1);
    const DD = pad(date.getDate());
    const HH = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${YYYY}-${MM}-${DD}T${HH}:${mm}`;
  };

  const handleEdit = (mc) => {
    setFormData({
      title: mc.title,
      description: mc.description || "",
      instructor_ids: mc.instructors ? mc.instructors.map((sp) => sp.id) : [],
      day: mc.day || 1,
      start_time: formatDateForInput(mc.start_time),
      end_time: formatDateForInput(mc.end_time),
      location: mc.location || "",
      capacity: mc.capacity || 30,
      prerequisites: mc.prerequisites || "None",
      materials: mc.materials || "None",
    });
    setIsEditing(true);
    setCurrentId(mc.id);
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await adminAPI.deleteMasterclass(deleteId);
      await fetchData();
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Failed to delete", err);
      alert("Failed to delete masterclass");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        // Ensure ISO strings for API
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        event_id: parseInt(eventId) || 1, // Fallback or handle null
        // Note: backend expects event_id in payload?
        // Checking MasterclassCreate schema... it has event_id.
      };
      // Add event_id explicitly
      payload.event_id = eventId ? parseInt(eventId) : 1;

      if (isEditing) {
        await adminAPI.updateMasterclass(currentId, payload);
      } else {
        await adminAPI.createMasterclass(payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("Failed to save", err);
      alert("Failed to save masterclass. Check console for details.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInstructorChange = (e) => {
    const speakerId = parseInt(e.target.value);
    if (!speakerId) return;

    if (!formData.instructor_ids.includes(speakerId)) {
      setFormData((prev) => ({
        ...prev,
        instructor_ids: [...prev.instructor_ids, speakerId],
      }));
    }
    // Reset dropdown
    e.target.value = "";
  };

  const removeInstructor = (id) => {
    setFormData((prev) => ({
      ...prev,
      instructor_ids: prev.instructor_ids.filter((sid) => sid !== id),
    }));
  };

  const getStatusBadgeClass = (mc) => {
    const reg = mc.current_registrations || 0;
    if (reg >= mc.capacity) return "badge-full";
    if (reg >= mc.capacity * 0.8) return "badge-limited";
    return "badge-available";
  };

  return (
    <div className="admin-page">
      <div
        className="admin-section-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: "700" }}>
          Masterclass Management
        </h1>
        <button className="btn-admin btn-admin-primary" onClick={handleAddNew}>
          <Plus size={18} />
          <span>Add Masterclass</span>
        </button>
      </div>

      <div className="admin-card">
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Instructor</th>
                <th>Time</th>
                <th>Capacity</th>
                <th>Waitlist</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : masterclasses.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    No masterclasses found.
                  </td>
                </tr>
              ) : (
                masterclasses.map((mc) => (
                  <tr key={mc.id}>
                    <td>
                      <div style={{ fontWeight: "600", color: "#0f172a" }}>
                        {mc.title}
                      </div>
                    </td>
                    <td>
                      {mc.instructors && mc.instructors.length > 0 ? (
                        <div style={{ fontSize: "0.85rem" }}>
                          {mc.instructors.map((sp) => sp.name).join(", ")}
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                          TBD
                        </span>
                      )}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: "0.8rem",
                        }}
                      >
                        <Clock size={14} />
                        {new Date(mc.start_time).toLocaleString([], {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </div>
                    </td>
                    <td>
                      {mc.current_registrations || 0} / {mc.capacity}
                    </td>
                    <td>{mc.waitlist_count || 0}</td>
                    <td>
                      <span
                        className={`admin-badge ${getStatusBadgeClass(mc)}`}
                      >
                        {mc.current_registrations >= mc.capacity
                          ? "FULL"
                          : "AVAILABLE"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "12px" }}>
                        {/* <button
                          className="btn-icon-text"
                          title="View Registrations"
                        >
                          <Users size={18} />
                          <span
                            style={{ fontSize: "0.75rem", marginLeft: "4px" }}
                          >
                            View
                          </span>
                        </button> */}
                        <button
                          className="btn-icon"
                          title="Edit"
                          onClick={() => handleEdit(mc)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon text-danger"
                          title="Delete"
                          onClick={() => handleDeleteClick(mc.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? "Edit Masterclass" : "Add Masterclass"}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h4>Basic Information</h4>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Advanced AI Prompt Engineering"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Provide a brief overview of the masterclass..."
              />
            </div>
          </div>

          <div className="form-section">
            <h4>Logistics & Capacity</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Instructors</label>
                <div
                  className="instructor-pills"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  {formData.instructor_ids.map((id) => {
                    const speaker = speakersList.find((s) => s.id === id);
                    return (
                      <div
                        key={id}
                        className="instructor-pill"
                        style={{
                          background: "#f1f5f9",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "0.85rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <span>{speaker?.name || "Unknown"}</span>
                        <X
                          size={14}
                          style={{ cursor: "pointer", color: "#64748b" }}
                          onClick={() => removeInstructor(id)}
                        />
                      </div>
                    );
                  })}
                  {formData.instructor_ids.length === 0 && (
                    <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                      No instructors selected
                    </span>
                  )}
                </div>
                <select
                  className="form-control"
                  onChange={handleInstructorChange}
                  value=""
                >
                  <option value="">Add Instructor...</option>
                  {speakersList
                    .filter((sp) => !formData.instructor_ids.includes(sp.id))
                    .map((sp) => (
                      <option key={sp.id} value={sp.id}>
                        {sp.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label>Capacity</label>
                <input
                  type="number"
                  name="capacity"
                  className="form-control"
                  value={formData.capacity}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  name="start_time"
                  className="form-control"
                  value={formData.start_time}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  name="end_time"
                  className="form-control"
                  value={formData.end_time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  className="form-control"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g. Conference Room B"
                  required
                />
              </div>
              {(() => {
                const start = currentEvent?.start_date
                  ? new Date(currentEvent.start_date)
                  : null;
                const end = currentEvent?.end_date
                  ? new Date(currentEvent.end_date)
                  : null;

                if (!start || !end) return null;

                // Strip time to calculate calendar days correctly
                const sDate = new Date(start).setHours(0, 0, 0, 0);
                const eDate = new Date(end).setHours(0, 0, 0, 0);
                const diffDays = Math.round(Math.abs(eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;

                if (diffDays <= 1) return null;

                const dayOptions = Array.from(
                  { length: diffDays },
                  (_, i) => i + 1,
                );

                return (
                  <div className="form-group">
                    <label>Day (1 to {diffDays})</label>
                    <select
                      name="day"
                      className="form-control"
                      value={formData.day}
                      onChange={handleChange}
                    >
                      {dayOptions.map((d) => (
                        <option key={d} value={d}>
                          Day {d}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="form-section">
            <h4>Requirements & Materials</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Prerequisites</label>
                <input
                  type="text"
                  name="prerequisites"
                  className="form-control"
                  value={formData.prerequisites}
                  onChange={handleChange}
                  placeholder="e.g. Basic JS knowledge"
                />
              </div>
              <div className="form-group">
                <label>Materials</label>
                <input
                  type="text"
                  name="materials"
                  className="form-control"
                  value={formData.materials}
                  onChange={handleChange}
                  placeholder="e.g. Laptop, Notebook"
                />
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-admin btn-admin-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-admin btn-admin-primary">
              {isEditing ? "Save Changes" : "Create Masterclass"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
        size="sm"
      >
        <div style={{ padding: "0 0 20px" }}>
          <p>
            Are you sure you want to delete this masterclass? This action cannot
            be undone.
          </p>
        </div>
        <div className="modal-actions">
          <button
            className="btn-admin btn-admin-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn-admin btn-admin-primary"
            style={{ background: "#ef4444", border: "none" }}
            onClick={confirmDelete}
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MasterclassManager;
